import type { ReactNode } from 'react';

import { styled } from 'styled-components';

const LabelSC = styled.label`
  display: block;
  width: 100%;
  text-align: center;
`;

const LabelWrapperSC = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const LabelText = styled.span<{ $darkMode?: boolean }>`
  color: ${(props) => (props.$darkMode ? props.theme.colors.white : props.theme.colors.oxfordBlue[100])};
  font-weight: 600;
  font-size: 1rem;
`;

const LabelValue = styled.span<{ $darkMode?: boolean }>`
  color: ${(props) => (props.$darkMode ? props.theme.colors.oxfordBlue[50] : props.theme.colors.erieBlack[100])};
  background-color: ${(props) =>
    props.$darkMode ? props.theme.colors.oxfordBlue[80] : props.theme.colors.slateGray[10]};
  font-size: 0.75rem;
  line-height: 0.875rem;
  padding: 0.3125rem 0.5rem;
  border-radius: 3.75rem;
  min-width: 3rem;
`;

const ActualValue = styled.span`
  color: ${(props) => props.theme.colors.oxfordBlue[50]};
  font-size: 0.875rem;
  line-height: 1.0625rem;
  display: block;
  margin-bottom: 0.9375rem;
`;

const InputWrapper = styled.span`
  display: block;
  margin: 0.875rem 0;
  line-height: 0;
`;

const InputSlider = styled.input.attrs({ type: 'range' })<{ $darkMode?: boolean; $progressInPercent: number }>`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 0.25rem;
  border-radius: 0.125rem;
  background: ${(props) =>
    `linear-gradient(to right, ${props.theme.colors.emeraldGreen[100]} 0%, ${props.theme.colors.emeraldGreen[100]} ${
      props.$progressInPercent
    }%, ${props.$darkMode ? props.theme.colors.shadows.oxfordBlue[30] : props.theme.colors.shadows.oxfordBlue[30]} ${
      props.$progressInPercent
    }%, ${
      props.$darkMode ? props.theme.colors.shadows.oxfordBlue[30] : props.theme.colors.shadows.oxfordBlue[30]
    } 100%);`};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 50%;
    background: ${(props) => props.theme.colors.white};
    box-shadow: ${(props) =>
      `0 0.25rem 0.625rem ${
        props.$darkMode ? props.theme.colors.shadows.blue[100] : props.theme.colors.shadows.gray[40]
      }`};
  }

  &::-moz-range-thumb {
    -moz-appearance: none;
    appearance: none;
    border: none;
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: ${(props) =>
      `0 0.25rem 0.625rem ${
        props.$darkMode ? props.theme.colors.shadows.blue[100] : props.theme.colors.shadows.gray[40]
      }`};
  }

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-thumb {
    box-shadow: ${(props) =>
      `0 0 0 0.1875rem ${props.theme.colors.emeraldGreen[50]}, 0 0.25rem 0.625rem ${
        props.$darkMode ? props.theme.colors.shadows.blue[100] : props.theme.colors.shadows.gray[40]
      }`};
  }

  &:focus::-moz-range-thumb {
    box-shadow: ${(props) =>
      `0 0 0 0.1875rem ${props.theme.colors.emeraldGreen[50]}, 0 0.25rem 0.625rem ${
        props.$darkMode ? props.theme.colors.shadows.blue[100] : props.theme.colors.shadows.gray[40]
      }`};
  }
`;

export type SliderThreshold = {
  name: string;
  value: number;
};

export type SliderProps = {
  actualThresholdValue: number;
  children: ReactNode;
  darkMode?: boolean;
  'data-test-id'?: string;
  onChange: (thresholdValue: number) => void;
  thresholdRange: SliderThreshold[];
};

export const Slider = ({
  actualThresholdValue,
  children,
  darkMode,
  'data-test-id': dataTestId,
  onChange,
  thresholdRange,
}: SliderProps) => {
  const thresholdIndex = thresholdRange.findIndex((threshold) => threshold.value === actualThresholdValue);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = Number(event.target.value);

    onChange(thresholdRange[index].value);
  };

  return (
    <LabelSC data-test-id={dataTestId}>
      <LabelWrapperSC>
        <LabelText $darkMode={darkMode}>{children}</LabelText>
        <LabelValue $darkMode={darkMode}>{thresholdRange[thresholdIndex].value}</LabelValue>
      </LabelWrapperSC>
      <ActualValue>{thresholdRange[thresholdIndex].name}</ActualValue>
      <InputWrapper>
        <InputSlider
          $darkMode={darkMode}
          $progressInPercent={(thresholdIndex / (thresholdRange.length - 1)) * 100}
          max={thresholdRange.length - 1}
          min={0}
          onChange={handleChange}
          value={thresholdIndex}
        />
      </InputWrapper>
    </LabelSC>
  );
};
